import { useState, useEffect, useRef } from 'react'

interface AuditContext {
  [key: string]: any
}

interface UseAuditContextReturn {
  currentAuditContext: AuditContext | null
  clearAuditContext: () => void
  setAuditContext: (context: AuditContext) => void
}

const useAuditContext = (): UseAuditContextReturn => {
  const [currentAuditContext, setCurrentAuditContext] = useState<AuditContext | null>(null)
  const lastContextRef = useRef<string | null>(null)

  useEffect(() => {
    const checkAuditContext = () => {
      const context = localStorage.getItem('currentAuditContext')
      
      if (context !== lastContextRef.current) {
        lastContextRef.current = context
        
        if (context) {
          try {
            const parsedContext = JSON.parse(context)
            setCurrentAuditContext(parsedContext)
          } catch (error) {
            console.error('Error parsing audit context:', error)
            localStorage.removeItem('currentAuditContext')
            setCurrentAuditContext(null)
            lastContextRef.current = null
          }
        } else {
          setCurrentAuditContext(null)
        }
      }
    }

    checkAuditContext()

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'currentAuditContext') {
        checkAuditContext()
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const clearAuditContext = () => {
    localStorage.removeItem('currentAuditContext')
    lastContextRef.current = null
    setCurrentAuditContext(null)
  }

  const setAuditContext = (context: AuditContext) => {
    const contextString = JSON.stringify(context)
    localStorage.setItem('currentAuditContext', contextString)
    lastContextRef.current = contextString
    setCurrentAuditContext(context)
  }

  return {
    currentAuditContext,
    clearAuditContext,
    setAuditContext
  }
}

export default useAuditContext 